import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { SidebarData } from './SidebarData';
import { useAuth } from '../context/AuthContext';
import './MobileBottomMenu.css';

const MobileBottomMenu = () => {
  const { userInfo } = useAuth();
  const role = userInfo?.role;

  const location = useLocation();
  const navigate = useNavigate();

  const [showMore, setShowMore] = useState(false);
  const [absenceCount, setAbsenceCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  const customerFeatures = useMemo(() => {
    return userInfo?.features || userInfo?.customer_features || {};
  }, [userInfo]);

  const hasFeature = (featureKey) => {
    if (!featureKey) return true;
    if (role === 'eigenaar') return true;
    return customerFeatures?.[featureKey] === true;
  };

  const isMenuItemVisible = (item) => {
    if (!item.roles.includes(role)) return false;
    if (!hasFeature(item.feature)) return false;

    if (
      item.title === 'Locaties' &&
      role === 'super_admin' &&
      userInfo?.is_single_user
    ) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!role) {
      setUnreadCount(0);
      return;
    }

    const rolesWithMsg = ['ouder', 'docent', 'super_admin', 'locatie_admin', 'eigenaar'];

    if (!rolesWithMsg.includes(role)) {
      setUnreadCount(0);
      return;
    }

    const abortController = new AbortController();

    const fetchUnread = async () => {
      try {
        const token = sessionStorage.getItem('jwtToken');

        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/messages/unreadcount`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortController.signal,
          }
        );

        setUnreadCount(res.data?.count || 0);
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Fout bij ophalen unread berichten:', err);
        }
      }
    };

    fetchUnread();

    return () => abortController.abort();
  }, [role]);

  useEffect(() => {
    if (!role) {
      setAbsenceCount(0);
      return;
    }

    const allowedRoles = ['super_admin', 'locatie_admin', 'docent'];

    if (!allowedRoles.includes(role)) {
      setAbsenceCount(0);
      return;
    }

    const abortController = new AbortController();

    const fetchAbsenceCount = async () => {
      try {
        const token = sessionStorage.getItem('jwtToken');

        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/absence/unreadcount`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortController.signal,
          }
        );

        setAbsenceCount(response.data?.count || 0);
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Fout bij ophalen afwezigheids-aantal:', error);
        }
      }
    };

    fetchAbsenceCount();

    return () => abortController.abort();
  }, [role]);

  const allItems = useMemo(() => {
    if (!role) return [];

    return SidebarData
      .filter(isMenuItemVisible)
      .map((item) => ({
        ...item,
        badgeCount:
          item.link === '/absences'
            ? absenceCount
            : item.link.startsWith('/messages')
            ? unreadCount
            : undefined,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, userInfo, customerFeatures, absenceCount, unreadCount]);

  const [mainItems, extraItems] = useMemo(() => {
    return [allItems.slice(0, 4), allItems.slice(4)];
  }, [allItems]);

  const handleNavigate = (link) => {
    navigate(link);
    setShowMore(false);
  };

  if (!userInfo) return null;

  return (
    <>
      <div className="mobile-bottom-menu d-md-none">
        <div className="menu-scroll">
          {mainItems.map((btn, idx) => (
            <button
              key={`main-${idx}`}
              onClick={() => handleNavigate(btn.link)}
              className={location.pathname === btn.link ? 'active' : ''}
              aria-label={btn.title}
            >
              <div className="icon-wrapper">
                <i className={btn.icon}></i>
                {btn.badgeCount > 0 && <span className="badge">{btn.badgeCount}</span>}
              </div>
              <span className="label">{btn.title}</span>
            </button>
          ))}

          {extraItems.length > 0 && (
            <button
              onClick={() => setShowMore((prev) => !prev)}
              aria-label="Meer menu opties"
            >
              <i className="fas fa-ellipsis-h"></i>
              <span className="label">Meer</span>
            </button>
          )}
        </div>
      </div>

      {showMore && (
        <div className="extra-menu-overlay" onClick={() => setShowMore(false)}>
          <div className="extra-menu" onClick={(e) => e.stopPropagation()}>
            {extraItems.map((btn, idx) => (
              <div
                key={`extra-${idx}`}
                className="extra-item"
                onClick={() => handleNavigate(btn.link)}
                role="button"
                tabIndex={0}
                aria-label={btn.title}
              >
                <i className={btn.icon}></i>
                <span>{btn.title}</span>
                {btn.badgeCount > 0 && <span className="badge">{btn.badgeCount}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default MobileBottomMenu;
